import { Menu, MenuItem, Typeahead } from 'react-bootstrap-typeahead';
import styled, { css } from 'styled-components';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Feedback } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

interface ITypeaheadInputProps<T extends string> {
    disabled?: boolean;
    error?: string;
    onChange: (value: T | null) => void;
    onDoValidate?: (value?: T | null) => void;
    options: T[];
    placeholder?: string;
    value: T | null;
    variant?: 'outline' | 'flushed';
}

export function TypeaheadInput<T extends string>(props: FormContextProps<ITypeaheadInputProps<T>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => (
                <>
                    <StyledTypeahead
                        $hasError={Boolean(properties.error)}
                        $variant={properties.variant ?? 'flushed'}
                        disabled={properties.disabled}
                        id="typeahead"
                        key="input"
                        onChange={async (selected) => {
                            if (selected.length === 1) {
                                await properties.onChange(selected[0] as T);
                                if (properties.onDoValidate) {
                                    await properties.onDoValidate(selected[0] as T);
                                }
                            }
                        }}
                        onInputChange={async (value) => {
                            await properties.onChange((value as T) || null);
                            if (properties.onDoValidate) {
                                await properties.onDoValidate((value as T) || null);
                            }
                        }}
                        options={properties.options}
                        placeholder={properties.placeholder}
                        renderMenu={(
                            results,
                            { newSelectionPrefix, paginationText, renderMenuItemChildren, ...menuProps }
                        ) => (
                            <Menu {...menuProps}>
                                {results.map((result, index) => (
                                    <MenuItem
                                        key={`${result}`}
                                        option={result}
                                        position={index}
                                    >
                                        {result as string}
                                    </MenuItem>
                                ))}
                            </Menu>
                        )}
                        selected={properties.value ? [properties.value] : []}
                    />
                    <Feedback
                        error={properties.error}
                        key="feedback"
                    />
                </>
            )}
        </FormContextConsumer>
    );
}

export function TypeaheadField<T extends string>(props: FormContextProps<ITypeaheadInputProps<T> & IFieldProps>) {
    return (
        <Field {...props}>
            <TypeaheadInput<T> {...props} />
        </Field>
    );
}

export const StyledTypeahead = styled(Typeahead)<{ $hasError: boolean; $variant: 'flushed' | 'outline' }>`
    .form-control {
        background-size: unset;

        ${(props) =>
            props.$variant === 'flushed' &&
            css`
                border-bottom: 1px solid ${props.theme.colors.neutral400};
                background-color: ${props.theme.colors.transparent};
                padding: 8px 0;
                border-radius: 0;

                &:hover {
                    border-color: ${props.theme.colors.neutral400};
                }

                &:focus {
                    outline: none;
                    border-color: ${props.theme.colors.neutral400};
                }
            `}

        ${(props) =>
            props.$variant === 'outline' &&
            css`
                border: 1px solid
                    ${props.$hasError ? `var(--kds-color-background-warning)` : `var(--kds-color-border-medium)`};
                background-color: white;
                padding: 8px 12px;
                border-radius: ${props.theme.radii.base};

                &:hover {
                    border-color: var(--kds-color-border-strong);
                }

                &:focus {
                    outline: none;
                    box-shadow: ${props.$hasError
                        ? `0 0 0 4px var(--kds-color-shadow-warning)`
                        : `0 0 0 4px var(--kds-color-shadow-focused)`};
                    border-color: var(--kds-color-border-focused);
                }
            `}
    }
`;
